import { useFormContext } from "react-hook-form";
import { Key, Briefcase } from "lucide-react";

import FormField from "./ui/FormField";
import Input from "./ui/Input";
import PasswordInput from "./ui/PasswordInput";
import Select from "./ui/Select";
import {
  LIFECYCLE_PLATFORM_DROPDOWN_OPTIONS,
  LIFECYCLE_PLATFORM,
} from "../utils/constants";

const ProjectLifecycleConfig = () => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();

  const selectedWorkItemPlatform = watch("work_item_adapter_type");

  return (
    <div className="w-full! border! border-slate-200! rounded-3xl! p-8! mb-5! bg-slate-50/50!">
      {/* Section Header */}
      <div className="flex items-center gap-3! mb-5! pb-4! border-b! border-slate-200!">
        <div className="bg-indigo-100! p-2.5! rounded-xl!">
          <Briefcase className="text-indigo-600!" size={20} />
        </div>
        <div>
          <h4 className="text-lg font-bold! text-slate-800! m-0!">
            Project Lifecycle Configuration
          </h4>
          <p className="text-sm text-slate-500! m-0! mt-0.5!">
            Configure your work item and sprint tracking source.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8! w-full!">
        <FormField
          label="Project Lifecycle Platform"
          error={errors.work_item_adapter_type}
          required
        >
          <Select
            {...register("work_item_adapter_type", {
              required: "Platform is required",
            })}
            options={LIFECYCLE_PLATFORM_DROPDOWN_OPTIONS}
          />
        </FormField>

        {/* CONDITIONALLY RENDERED: Project Lifecycle PAT */}
        {selectedWorkItemPlatform && selectedWorkItemPlatform !== "" && (
          <FormField
            label="Personal Access Token (PAT) / API Token"
            icon={Key}
            error={errors.work_item_adapter_pat}
            required
          >
            <PasswordInput
              {...register("work_item_adapter_pat", {
                required: selectedWorkItemPlatform
                  ? "Project Lifecycle Platform's PAT is required"
                  : false,
              })}
              placeholder="Enter your access token"
            />
          </FormField>
        )}
      </div>

      {/* CONDITIONALLY RENDERED: Azure DevOps Org and Project Row */}
      {selectedWorkItemPlatform === LIFECYCLE_PLATFORM.AZURE_DEVOPS && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8! w-full!">
          <FormField
            label="Azure DevOps Organization"
            error={errors.azure_devops_org}
            required
          >
            <Input
              {...register("azure_devops_org", {
                required:
                  selectedWorkItemPlatform === LIFECYCLE_PLATFORM.AZURE_DEVOPS
                    ? "Organization is required"
                    : false,
              })}
              placeholder="e.g. my-org"
            />
          </FormField>
          <FormField
            label="Azure DevOps Project"
            error={errors.azure_devops_project}
            required
          >
            <Input
              {...register("azure_devops_project", {
                required:
                  selectedWorkItemPlatform === LIFECYCLE_PLATFORM.AZURE_DEVOPS
                    ? "Project is required"
                    : false,
              })}
              placeholder="e.g. my-project"
            />
          </FormField>
        </div>
      )}

      {/* CONDITIONALLY RENDERED: Jira Fields */}
      {selectedWorkItemPlatform === LIFECYCLE_PLATFORM.JIRA && (
        <>
          <FormField label="Jira Email" error={errors.jira_email} required>
            <Input
              {...register("jira_email", {
                required:
                  selectedWorkItemPlatform === LIFECYCLE_PLATFORM.JIRA
                    ? "Email is required"
                    : false,
              })}
              placeholder="e.g. user@example.com"
            />
          </FormField>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8! w-full!">
            <FormField
              label="Jira Base URL"
              error={errors.jira_base_url}
              required
            >
              <Input
                {...register("jira_base_url", {
                  required:
                    selectedWorkItemPlatform === LIFECYCLE_PLATFORM.JIRA
                      ? "Jira Base URL is required"
                      : false,
                })}
                placeholder="e.g. https://your-org.atlassian.net"
              />
            </FormField>
            <FormField
              label="Jira Project Key"
              error={errors.jira_project_key}
              required
            >
              <Input
                {...register("jira_project_key", {
                  required:
                    selectedWorkItemPlatform === LIFECYCLE_PLATFORM.JIRA
                      ? "Project key is required"
                      : false,
                })}
                placeholder="e.g. JADE"
              />
            </FormField>
          </div>
        </>
      )}

      {/* Original Lifecycle Fields */}
      <FormField
        label="Release Tag / Sprint"
        error={errors.release_tag}
        required
      >
        <Input
          {...register("release_tag", { required: "Release tag is required" })}
          placeholder="e.g. Sprint 12"
        />
      </FormField>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8! w-full!">
        <FormField label="Date From" error={errors.date_from}>
          <Input type="date" {...register("date_from")} />
        </FormField>
        <FormField label="Date To" error={errors.date_to}>
          <Input type="date" {...register("date_to")} />
        </FormField>
      </div>

      <FormField label="Documentation Release Date">
        <Input type="date" {...register("release_date")} />
      </FormField>
    </div>
  );
};

export default ProjectLifecycleConfig;
